import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Canvas, useThree, type ThreeEvent } from '@react-three/fiber';
import { ContactShadows, Environment, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import type {
  TacticsCoachingPoint,
  TacticsLayout,
  TacticsZone,
  TacticsDrawing as TacticsLayoutDrawings,
} from '@4cm/core/tactics';
import { mirrorTacticsLayout } from '@4cm/core/tactics/mirror';
import { computeTeamCompactness } from '@4cm/core/tactics/compactness';
import { encodeTacticCode, decodeTacticCode } from '@4cm/core/tactics/tacticCodes';
import { inferRoleAtPosition } from '@4cm/core/tactics/positionRoles';
import {
  appendBriefHistory,
  generateAllPlayerBriefs,
  type PlayerBrief,
} from '@4cm/core/tactics/playerBriefs';
import { applyTeamShapeTransform } from '@4cm/core/tactics/teamShape';
import { polishAllPlayerBriefs } from '../../lib/playerBriefPolish';
import {
  defaultOverlaysState,
  computeVoronoiCells,
  computePassNetwork,
  computeTeamShapeBands,
  computeOffsideLines,
  computeDefensiveShape,
  computeHeatmap,
  computePitchControl,
  type TacticsOverlaysState,
} from '@4cm/core/tactics/overlays';
import {
  defaultBundle,
  type TeamTacticsBundle,
} from '@4cm/core/tactics/teamTactics';
import type { PlayerRoleSelection } from '@4cm/core/tactics/roles';
import {
  clampStrokeWidth,
  defaultDrawing,
  type ArrowHead,
  type DashStyle,
  type DrawingKind,
  type TacticsDrawing,
} from '@4cm/core/tactics/drawings';
import {
  SET_PIECE_TYPES,
  defaultSetPiece,
  type SetPieceType,
  type TacticsSetPiece,
} from '@4cm/core/tactics/setPieces';
import { CORNER_TEMPLATES } from '@4cm/core/tactics/cornerTemplates';
import {
  EXTENDED_FORMATIONS,
  mirrorFormationForAway,
} from '@4cm/core/tactics/formationsExtended';
import { getSetPiece } from '../../lib/setPiecesStore';
import { listCustomFormations, saveCustomFormation } from '../../lib/customFormationsStore';
import { loadLocalPlayers } from '../../lib/demoData';
import { FieldMesh } from './FieldMesh';
import { PlayerToken3D } from './PlayerToken3D';
import { BallMesh } from './BallMesh';
import { Goal } from './Goal';
import { Lighting } from './Lighting';
import { MovementArrow3D, type MovementArrowSpec } from './MovementArrow3D';
import { ZoneLayer3D } from './ZoneLayer3D';
import { CameraRig, type OrbitControlsHandle } from './CameraRig';
import {
  CAMERA_PRESETS,
  DEFAULT_CAMERA_PRESET,
  type CameraPresetId,
} from './cameraPresets';
import {
  DEFAULT_FORMATION,
  FIELD_LENGTH,
  FIELD_WIDTH,
  projectFormation,
  type PlayerSpot,
} from './formationProjection';
import {
  clearTacticsLayout,
  loadTacticsLayout,
  onTacticsLayoutChanged,
  saveTacticsLayout,
} from '../../lib/tactics3dStore';
import { getPattern, savePattern } from '../../lib/tacticsPatternsStore';
import { useTacticsHistory } from '../../lib/useTacticsHistory';
import { useWalkthroughPlayback } from '../../lib/useWalkthroughPlayback';
import {
  FormationDropdown,
  HudChip,
  HudHint,
  HudRow,
  HudShell,
  SurfacePills,
  RunPlaybackControls,
  SavedAgo,
  useReducedMotion,
  WalkthroughControls,
  RoleInstructionsPicker,
  TeamTacticsPanel,
  SetPieceDrawer,
  FormationLibraryDrawer,
  DrawingToolbar,
  OverlayToggles,
  CompactnessChips,
  PositionSlotPicker,
  PlayerBriefsDrawer,
  TacticMenu,
  LibraryMenu,
} from '../tacticsHud';
import { CoachingPointPin } from '../tacticsHud/CoachingPointPin';
import { PatternDrawer } from '../tacticsHud/PatternDrawer';
// 3D drawings + Voronoi overlay are rendered via an absolutely-
// positioned SVG above the R3F canvas (Option A in the Phase 4 wave-B
// brief). Every overlay + drawing is rendered as R3F geometry inside
// the Canvas so the 3D camera projects them with real perspective —
// no 2D SVG overlay plastered on top any more.
import { VoronoiLayer3D } from './VoronoiLayer3D';
import { HalfSpacesLayer3D } from './HalfSpacesLayer3D';
import { TeamShapeLayer3D } from './TeamShapeLayer3D';
import { PositionalPlayGridLayer3D } from './PositionalPlayGridLayer3D';
import { XTLayer3D } from './XTLayer3D';
import {
  DefensiveShapeLayer3D,
  OffsideLineLayer3D,
  PassNetLayer3D,
} from './AnalyticalOverlays3D';
import { HeatmapLayer3D } from './HeatmapLayer3D';
import { PitchControlLayer3D } from './PitchControlLayer3D';
import { DrawingLayer3D } from './DrawingLayer3D';
import { ZonePolygonOverlay3D } from './ZonePolygonOverlay3D';
import { DrawingCaptureMesh } from './DrawingCaptureMesh';
import './TacticsBoard3D.css';

// Keep a dragged token comfortably inside the touchlines.
const X_LIMIT = FIELD_WIDTH / 2 - 1;
const Z_LIMIT = FIELD_LENGTH / 2 - 1;
function clampToPitch(spot: PlayerSpot, x: number, z: number): PlayerSpot {
  return {
    ...spot,
    x: Math.max(-X_LIMIT, Math.min(X_LIMIT, x)),
    z: Math.max(-Z_LIMIT, Math.min(Z_LIMIT, z)),
  };
}

// Plane at the grass surface for projecting the pointer when sketching
// arrows. Lives inside the canvas so it can use useThree.
const ARROW_DRAW_PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

/**
 * Cheap probe for "this browser/tab cannot give us a WebGL context".
 * Used to render the fallback link to the 2D board instead of a blank
 * canvas. We create a throwaway canvas (NOT mounted) and ask it for a
 * WebGL2/WebGL context — null = unavailable. Some browsers fall through
 * to a software renderer that *technically* succeeds; we accept those
 * because the 3D scene will at least limp along.
 *
 * Test note: happy-dom / jsdom return null for every getContext call,
 * which would force every existing test through the fallback path.
 * Vitest sets `import.meta.env.VITEST` so we use that to short-circuit
 * the probe in tests — tests that *want* to exercise the fallback path
 * can call `__forceWebGLUnavailable(true)` below.
 */
let webglProbeOverride: boolean | null = null;
export function __forceWebGLUnavailable(force: boolean): void {
  webglProbeOverride = force ? false : null;
}
function detectWebGLSupport(): boolean {
  if (webglProbeOverride !== null) return webglProbeOverride;
  // Vitest's import.meta.env.VITEST is the canonical "we're in a test"
  // signal. Anything that explicitly wants to verify the fallback flips
  // the override above.
  const meta = import.meta as ImportMeta & { env?: { VITEST?: unknown } };
  if (meta.env?.VITEST) return true;
  if (typeof document === 'undefined') return false;
  try {
    const probe = document.createElement('canvas');
    const ctx =
      probe.getContext('webgl2') ||
      probe.getContext('webgl') ||
      probe.getContext('experimental-webgl');
    return ctx !== null;
  } catch {
    return false;
  }
}

interface ArrowDrawLayerProps {
  active: boolean;
  color: string;
  /** When set, the next pointer-down (anywhere on the pitch capture
   *  plane) seeds the arrow with this start point — used when the
   *  coach pressed on a player token to begin an arrow there.
   *  Cleared by the layer once consumed. */
  seedOrigin: { x: number; z: number } | null;
  onSeedConsumed: () => void;
  onArrowComplete: (points: Array<{ x: number; z: number }>) => void;
  onDragChange: (dragging: boolean) => void;
}
function ArrowDrawLayer({ active, color, seedOrigin, onSeedConsumed, onArrowComplete, onDragChange }: ArrowDrawLayerProps) {
  const { camera, raycaster, pointer } = useThree();
  const drawingRef = useRef(false);
  const pointsRef = useRef<Array<{ x: number; z: number }>>([]);
  // Live preview of the arrow being drawn — updates per pointer move so
  // the coach sees the curve as they sketch.
  const [preview, setPreview] = useState<MovementArrowSpec | null>(null);

  const projectPointer = useCallback(() => {
    raycaster.setFromCamera(pointer, camera);
    const target = new THREE.Vector3();
    raycaster.ray.intersectPlane(ARROW_DRAW_PLANE, target);
    return { x: target.x, z: target.z };
  }, [camera, pointer, raycaster]);

  // Sample the cursor every ~0.5m along the path so the curve has enough
  // points to feel hand-drawn but not so many that the tube gets noisy.
  const MIN_SEGMENT_DIST = 0.6;

  // If a player token reported a seed origin (i.e. the coach pressed on
  // them in draw mode), bootstrap the draw state immediately so the
  // very next pointer-move on the capture plane extends the curve.
  useEffect(() => {
    if (!active || !seedOrigin) return;
    drawingRef.current = true;
    pointsRef.current = [seedOrigin];
    setPreview({ id: 'preview', points: [seedOrigin], color });
    onDragChange(true);
    onSeedConsumed();
  }, [active, seedOrigin, color, onDragChange, onSeedConsumed]);

  const onDown = (e: ThreeEvent<PointerEvent>) => {
    if (!active) return;
    e.stopPropagation();
    // If we were already seeded by a player-token press, keep that
    // origin and add the current cursor as the second point.
    if (drawingRef.current) {
      pointsRef.current = [...pointsRef.current, projectPointer()];
      setPreview({ id: 'preview', points: pointsRef.current, color });
      (e.target as Element).setPointerCapture?.(e.pointerId);
      return;
    }
    drawingRef.current = true;
    pointsRef.current = [projectPointer()];
    setPreview({ id: 'preview', points: [...pointsRef.current], color });
    onDragChange(true);
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onMove = (e: ThreeEvent<PointerEvent>) => {
    if (!active || !drawingRef.current) return;
    e.stopPropagation();
    const next = projectPointer();
    const last = pointsRef.current[pointsRef.current.length - 1];
    const d = Math.hypot(next.x - last.x, next.z - last.z);
    if (d < MIN_SEGMENT_DIST) return;
    pointsRef.current = [...pointsRef.current, next];
    setPreview({ id: 'preview', points: pointsRef.current, color });
  };

  const onUp = (e: ThreeEvent<PointerEvent>) => {
    if (!active || !drawingRef.current) return;
    e.stopPropagation();
    drawingRef.current = false;
    onDragChange(false);
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    const final = pointsRef.current;
    pointsRef.current = [];
    setPreview(null);
    if (final.length >= 2) onArrowComplete(final);
  };

  return (
    <>
      {/* Pointer-catching plane covers the whole pitch + a margin so
          arrows can start/end near the touchline. Renders only when
          draw mode is active; in move mode it disappears so player
          tokens / OrbitControls aren't shadowed. */}
      {active && (
        <mesh
          position={[0, 0.0005, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          <planeGeometry args={[FIELD_WIDTH * 1.2, FIELD_LENGTH * 1.2]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      )}
      {preview && <MovementArrow3D arrow={preview} />}
    </>
  );
}

export interface TacticsBoard3DProps {
  /** Initial home formation. Caller can drive it via key/state if they want a controlled board. */
  homeFormation?: string;
  /** Show an away team mirrored across the halfway line. */
  showAway?: boolean;
  /** Initial away formation. */
  awayFormation?: string;
  /**
   * Opaque key (typically `tactics-3d-${sessionId}`) under which the
   * board persists drag positions + sketched arrows to localStorage.
   * Omit to render an ephemeral board that doesn't write to disk.
   */
  persistKey?: string;
  /**
   * Optional R3F children mounted inside the same `<Canvas>` after the
   * board's own scene. Used by the drill canvas to layer in role
   * players / cones / drill-specific arrows + animation without
   * duplicating the field, camera, or lighting.
   */
  children?: React.ReactNode;
  /** When set, the field mesh + standard markings are not rendered.
   *  Used by the drill canvas in 'custom' pitch view where the coach
   *  works on a 15×15m grid instead of a full pitch. */
  hideField?: boolean;
  /** When set, the tactics HUD chrome (mode chips, view presets,
   *  formation pickers, stats toggles) is not rendered — only the
   *  Canvas itself. Used by the drill canvas which has its own
   *  controls and shouldn't surface Josh's tactics-specific UI. */
  hideHud?: boolean;
}

/**
 * Public component boundary for the 3D tactics surface. Internals (R3F /
 * three.js) are intentionally swappable — see docs/spec/16-tactics-board-3d.md.
 */
export function TacticsBoard3D({
  homeFormation = DEFAULT_FORMATION,
  showAway: showAwayInitial = true,
  awayFormation = DEFAULT_FORMATION,
  persistKey,
  children,
  hideField,
  hideHud,
}: TacticsBoard3DProps) {
  // Internal toggle — coach flips opponent visibility via the View row chip.
  // Initial value comes from the prop so callers can default to home-only.
  const [showAway, setShowAway] = useState<boolean>(showAwayInitial);
  // On first mount with a persistKey, hydrate from localStorage if there's
  // a saved layout for this session. Otherwise use defaults from props.
  // Hydration shape captured up front so subsequent renders don't re-read.
  const initial = useMemo(() => {
    if (!persistKey) return null;
    return loadTacticsLayout(persistKey);
  }, [persistKey]);

  const [home, setHome] = useState(initial?.homeFormation ?? homeFormation);
  const [away, setAway] = useState(initial?.awayFormation ?? awayFormation);
  const [view, setView] = useState<CameraPresetId>(DEFAULT_CAMERA_PRESET);
  // Re-snap on the same preset by bumping a nonce — the rig effect re-runs.
  const [snapNonce, setSnapNonce] = useState(0);
  const controlsRef = useRef<OrbitControlsHandle | null>(null);

  // Live spot state — formation preset seeds it, drag mutates it. Reseeds
  // whenever the coach picks a new formation. If we hydrated saved spots,
  // those win over the formation seed.
  const [homeSpots, setHomeSpots] = useState<PlayerSpot[]>(
    () => initial?.homeSpots ?? projectFormation(home, 'home'),
  );
  const [awaySpots, setAwaySpots] = useState<PlayerSpot[]>(
    () => initial?.awaySpots ?? projectFormation(away, 'away'),
  );
  // Track whether the formation change is coach-initiated (true) vs the
  // initial render (false) so we don't clobber hydrated spots on mount.
  const formationChangedRef = useRef(false);
  useEffect(() => {
    if (formationChangedRef.current) {
      setHomeSpots(projectFormation(home, 'home'));
    }
  }, [home]);
  useEffect(() => {
    if (formationChangedRef.current) {
      setAwaySpots(projectFormation(away, 'away'));
    }
  }, [away]);

  // Currently-selected token (encoded as "team-index"). Single selection.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // OrbitControls fights the per-token drag if both want the pointer at
  // the same time. Disable orbit while a drag is in flight.
  const [orbitEnabled, setOrbitEnabled] = useState(true);

  // Interaction mode toggle. 'move' = drag tokens. 'draw' = sketch arrows
  // on the pitch (per spec §16 — TubeGeometry along Catmull-Rom curve).
  const [mode, setMode] = useState<'move' | 'draw'>('move');
  const [arrows, setArrows] = useState<MovementArrowSpec[]>(() => initial?.arrows ?? []);
  // Phase 3 — annotation slots. No interactive editing in this commit;
  // we render whatever the layout carries (v=1 entries arrive with
  // these fields undefined, hence the `?? []` fallback).
  const [zones, setZones] = useState<TacticsZone[]>(() => initial?.zones ?? []);
  const [coachingPoints, setCoachingPoints] = useState<TacticsCoachingPoint[]>(
    () => initial?.coachingPoints ?? [],
  );
  // Phase 4 — drawer open state. Board-local; the trigger HudChip
  // toggles it and the drawer's onClose hook closes it.
  const [patternsOpen, setPatternsOpen] = useState(false);

  // Phase 4 — Design row state. Each surface is gated by a drawer /
  // mode flag so the HUD can keep them dormant when not in use; the
  // persisted slots live on the layout (round-trip via auto-save).
  const [playerRoles, setPlayerRoles] = useState<Record<string, PlayerRoleSelection>>(
    () => (initial?.playerRoles as Record<string, PlayerRoleSelection> | undefined) ?? {},
  );
  const [teamTactics, setTeamTactics] = useState<TeamTacticsBundle | undefined>(
    () => (initial?.teamTactics as TeamTacticsBundle | undefined) ?? undefined,
  );
  // Spatial-slider re-project flag — set when the coach edits a
  // slider via the TeamTacticsPanel below; listener / Brain paths
  // leave it false so loaded layouts don't get re-transformed.
  const teamShapeChangedRef = useRef(false);
  const homeSpatialKey = useMemo(() => {
    const side = teamTactics?.home;
    if (!side) return null;
    const active = side.slots.find((s) => s.id === side.activeSlotId);
    if (!active) return null;
    const s = active.tactics.sliders;
    return `${s.lineHeight ?? 50}|${s.width ?? 50}|${s.depth ?? 50}`;
  }, [teamTactics]);
  useEffect(() => {
    if (!teamShapeChangedRef.current) return;
    teamShapeChangedRef.current = false;
    const side = teamTactics?.home;
    if (!side) return;
    const active = side.slots.find((s) => s.id === side.activeSlotId);
    if (!active) return;
    setHomeSpots(
      applyTeamShapeTransform(
        projectFormation(home, 'home'),
        active.tactics,
        'home',
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeSpatialKey]);
  const [setPieces, setSetPieces] = useState<TacticsSetPiece[]>(
    () => ((initial?.setPieces ?? []) as unknown as TacticsSetPiece[]),
  );
  const [drawings, setDrawings] = useState<TacticsDrawing[]>(
    () => ((initial?.drawings ?? []) as unknown as TacticsDrawing[]),
  );
  const [overlays, setOverlays] = useState<TacticsOverlaysState>(
    () => (initial?.overlays as TacticsOverlaysState | undefined) ?? defaultOverlaysState(),
  );
  // Spacing focus — 3-way state: 'both' / 'home' / 'away'. Filters
  // the points fed into voronoi compute, so single-team focus
  // expands the chosen team's cells across the whole pitch.
  const [voronoiFocus, setVoronoiFocus] = useState<'both' | 'home' | 'away'>('both');

  // Drawer / panel toggles for the Design row.
  const [formationsOpen, setFormationsOpen] = useState(false);
  const [tacticsPanelOpen, setTacticsPanelOpen] = useState<false | 'home' | 'away'>(false);
  // Set pieces moved to /setpieces as a sibling surface. Drawer
  // remains mounted for the "Create from current board" deep-link
  // (/setpieces → /3d?openSetPieces=1).
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [setPiecesOpen, setSetPiecesOpen] = useState(
    () => searchParams.get('openSetPieces') === '1',
  );
  const [overlaysOpen, setOverlaysOpen] = useState(false);
  // v=5 — coach-side per-player briefs (mirror of TacticsBoard2D).
  const [briefsOpen, setBriefsOpen] = useState(false);
  const [playerBriefs, setPlayerBriefs] = useState<Record<string, PlayerBrief>>(
    () => (initial?.playerBriefs as Record<string, PlayerBrief> | undefined) ?? {},
  );
  const [playerBriefsStale, setPlayerBriefsStale] = useState<boolean>(
    () => initial?.playerBriefsStale ?? false,
  );
  const [playerBriefHistory, setPlayerBriefHistory] = useState<
    Record<string, PlayerBrief[]>
  >(
    () =>
      (initial?.playerBriefHistory as Record<string, PlayerBrief[]> | undefined) ??
      {},
  );
  // v=5 — coach-supplied roster identities. Persisted but not yet
  // surfaced on the 3D figure tokens; the 2D board owns the entry
  // UX via the Briefs drawer. State here keeps the round-trip
  // intact when coaches edit on 2D then look at 3D.
  const [roster, setRoster] = useState<Record<string, { name?: string; number?: number }>>(
    () => (initial?.roster as Record<string, { name?: string; number?: number }> | undefined) ?? {},
  );
  const [briefsBusy, setBriefsBusy] = useState(false);
  // Phase-mode editing — mirror of 2D. When 'phase' AND the
  // walkthrough is parked on a keyframe, drag commits update that
  // keyframe's spots instead of the base. Walkthrough state is
  // declared later in this file, so the active edit key flows through
  // a ref that's refreshed after walkthrough settles. Auto-detected
  // from walkthrough state — explicit chip toggle removed.
  const activeEditKeyframeIdRef = useRef<string | null>(null);
  // Same late-declaration dodge for the walkthrough reset: handlers
  // declared above the `walkthrough` object (e.g. handleApplySetPiece)
  // call through this ref, which an effect below keeps current.
  const walkthroughResetRef = useRef<() => void>(() => {});
  // Keyframes — local state so Phase-mode edits + Brain commits both
  // flow through the same path. Declared early so the save effect can
  // reference it without a TDZ error.
  const [keyframes, setKeyframes] = useState(
    () => (initial?.keyframes ?? []) as NonNullable<typeof initial>['keyframes'],
  );
  // Role mode: when true, next token tap opens the role picker for
  // that token. Toggle chip removed from the Design row; the state is
  // kept so future surfaces (set-piece roles, Brain-driven flows) can
  // flip it without re-introducing the open-play chip.
  const [roleMode] = useState(false);
  const [roleTarget, setRoleTarget] = useState<{ key: string; slot: string } | null>(null);
  // Drag-to-pick: after a drag commits to a different zone, store the
  // pending pick — the floating PositionSlotPicker reads this and the
  // coach confirms (or changes) the new slot.
  const [slotPicker, setSlotPicker] = useState<
    { key: string; currentSlot: string; suggested: string } | null
  >(null);

  // Draw mode + active drawing style. The toolbar mounts when
  // `drawingActiveKind !== null`; the SVG overlay below the HUD
  // captures pointer events and emits TacticsDrawing entries.
  const [drawingActiveKind, setDrawingActiveKind] = useState<DrawingKind | null>(null);
  const [drawingStroke, setDrawingStroke] = useState<string>('#ffffff');
  const [drawingStrokeWidth, setDrawingStrokeWidth] = useState<number>(0.35);
  const [drawingDash, setDrawingDash] = useState<DashStyle>('solid');
  const [drawingArrowHead, setDrawingArrowHead] = useState<ArrowHead>('standard');

  // External writers (Brain commits, future multi-tab sync) update
  // localStorage via saveTacticsLayout; this listener rehydrates the
  // board's local state so the new layout shows up immediately.
  // selfSaveRef short-circuits our OWN auto-save's emit so the
  // listener doesn't react to changes it caused itself — without
  // this, the auto-save → emit → listener → state-change loop
  // re-saves every 400ms forever.
  const selfSaveRef = useRef(false);
  useEffect(() => {
    if (!persistKey) return;
    return onTacticsLayoutChanged(persistKey, () => {
      if (selfSaveRef.current) {
        // Our own auto-save's emit; the state's already in sync.
        selfSaveRef.current = false;
        return;
      }
      const fresh = loadTacticsLayout(persistKey);
      if (!fresh) return;
      setHome(fresh.homeFormation);
      setAway(fresh.awayFormation);
      setHomeSpots(fresh.homeSpots);
      setAwaySpots(fresh.awaySpots);
      setArrows(fresh.arrows);
      // Phase 3 fields may be undefined on a v=1/v=2 entry — coalesce
      // to empty arrays so the scene always has something to render.
      setZones(fresh.zones ?? []);
      setCoachingPoints(fresh.coachingPoints ?? []);
      // Phase 4 — same defensive coalesce. Old v=1/v=2/v=3 entries
      // never carried these so the renderer falls back to empty
      // collections / fresh overlays state.
      setPlayerRoles((fresh.playerRoles as Record<string, PlayerRoleSelection> | undefined) ?? {});
      setTeamTactics((fresh.teamTactics as TeamTacticsBundle | undefined) ?? undefined);
      setSetPieces(((fresh.setPieces ?? []) as unknown as TacticsSetPiece[]));
      setDrawings(((fresh.drawings ?? []) as unknown as TacticsDrawing[]));
      setOverlays((fresh.overlays as TacticsOverlaysState | undefined) ?? defaultOverlaysState());
      // v=5 — briefs round-trip alongside the rest so Brain commits +
      // multi-tab edits + 2D ↔ 3D switches keep them in sync.
      setPlayerBriefs(
        (fresh.playerBriefs as Record<string, PlayerBrief> | undefined) ?? {},
      );
      setPlayerBriefsStale(fresh.playerBriefsStale ?? false);
      setPlayerBriefHistory(
        (fresh.playerBriefHistory as Record<string, PlayerBrief[]> | undefined) ?? {},
      );
      // Phase mode — Brain sequence commits update the keyframe set;
      // rehydrate so the walkthrough scrubber sees the new markers.
      setKeyframes(fresh.keyframes ?? []);
      setRoster(
        (fresh.roster as Record<string, { name?: string; number?: number }> | undefined) ?? {},
      );
    });
  }, [persistKey]);
  const [arrowColor, setArrowColor] = useState<'home' | 'away' | 'neutral'>('home');
  const arrowColorHex = arrowColor === 'home' ? '#4ade80' : arrowColor === 'away' ? '#fbbf24' : '#f5f5f5';
  // When a player token is pressed in draw mode it writes its position +
  // key here; the ArrowDrawLayer picks it up next render and seeds the
  // arrow with that origin. The playerKey rides along so the resulting
  // arrow can bind to the player and animate them along the curve on
  // playback.
  const [arrowSeed, setArrowSeed] = useState<
    { origin: { x: number; z: number }; playerKey: string | null } | null
  >(null);
  const [savedAt, setSavedAt] = useState<string | null>(initial?.saved_at ?? null);

  // ---- Player-brief handlers ----
  const handleRegenerateBriefs = useCallback(
    async (options: { polish: boolean } = { polish: false }) => {
      const layout: TacticsLayout = {
        v: 5,
        homeFormation: home,
        awayFormation: away,
        homeSpots,
        awaySpots,
        arrows,
        zones,
        coachingPoints,
        playerRoles,
        teamTactics: teamTactics as TacticsLayout['teamTactics'],
        setPieces: setPieces as unknown as TacticsLayout['setPieces'],
        drawings: drawings as unknown as TacticsLayout['drawings'],
        saved_at: savedAt ?? new Date().toISOString(),
      };
      const rules = generateAllPlayerBriefs(layout, 'home');
      const writeMap = (nextRules: PlayerBrief[]): Record<string, PlayerBrief> => {
        const out: Record<string, PlayerBrief> = {};
        for (const b of nextRules) {
          const prev = playerBriefs[b.playerKey];
          out[b.playerKey] = prev?.source === 'manual' ? prev : b;
        }
        return out;
      };
      let nextHistory = playerBriefHistory;
      for (const k of Object.keys(playerBriefs)) {
        const prior = playerBriefs[k];
        if (prior) nextHistory = appendBriefHistory(nextHistory, k, prior);
      }
      setPlayerBriefHistory(nextHistory);
      setPlayerBriefs(writeMap(rules));
      setPlayerBriefsStale(false);

      if (!options.polish) return;
      setBriefsBusy(true);
      try {
        const polished = await polishAllPlayerBriefs(rules, layout, 'home');
        setPlayerBriefs(writeMap(polished));
      } catch {
        /* polish is additive; rule output stands on failure */
      } finally {
        setBriefsBusy(false);
      }
    },
    [
      home,
      away,
      homeSpots,
      awaySpots,
      arrows,
      zones,
      coachingPoints,
      playerRoles,
      teamTactics,
      setPieces,
      drawings,
      savedAt,
      playerBriefs,
      playerBriefHistory,
    ],
  );
  const handleEditBrief = useCallback(
    (playerKey: string, next: PlayerBrief) => {
      setPlayerBriefs((prev) => {
        const prior = prev[playerKey];
        if (prior) {
          setPlayerBriefHistory((h) => appendBriefHistory(h, playerKey, prior));
        }
        return { ...prev, [playerKey]: next };
      });
    },
    [],
  );
  const handleRestoreBrief = useCallback(
    (playerKey: string, version: PlayerBrief) => {
      setPlayerBriefs((prev) => {
        const prior = prev[playerKey];
        if (prior) {
          setPlayerBriefHistory((h) => appendBriefHistory(h, playerKey, prior));
        }
        return { ...prev, [playerKey]: version };
      });
    },
    [],
  );

  // Stale-tracking effect — when brief-affecting inputs change AND we
  // already have at least one brief, flip stale=true.
  const briefsInputsKey = useMemo(
    () =>
      JSON.stringify({
        home,
        away,
        homeSpots,
        awaySpots,
        arrows,
        teamTactics,
        playerRoles,
        setPieces,
      }),
    [home, away, homeSpots, awaySpots, arrows, teamTactics, playerRoles, setPieces],
  );
  const briefsInputsRef = useRef(briefsInputsKey);
  useEffect(() => {
    if (briefsInputsKey === briefsInputsRef.current) return;
    briefsInputsRef.current = briefsInputsKey;
    if (Object.keys(playerBriefs).length > 0) setPlayerBriefsStale(true);
  }, [briefsInputsKey, playerBriefs]);

  // Playback state for run-path animation. progress is 0..1 across the
  // bound arrows' curves; PLAYBACK_DURATION_MS controls how fast a 30m
  // sprint reads as one. Pause holds at the current progress; Stop returns
  // to 0 and clears the playback override so tokens snap home.
  const PLAYBACK_DURATION_MS = 3200;
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // CatmullRom curves cached per arrow.id — rebuilding each frame torches
  // GC. The Three.js curves are pure-data lookups so reuse is safe across
  // renders. Recomputed only when arrows change.
  const arrowCurves = useMemo(() => {
    const map = new Map<string, THREE.CatmullRomCurve3>();
    for (const a of arrows) {
      if (!a.playerKey || a.points.length < 2) continue;
      const pts = a.points.map((p) => new THREE.Vector3(p.x, 0, p.z));
      map.set(a.id, new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.4));
    }
    return map;
  }, [arrows]);

  // Resolve playback override per player key. While playing, each bound
  // player's position is curve.getPointAt(progress). Returns a Map from
  // playerKey → world XZ.
  const playbackPositions = useMemo(() => {
    const out = new Map<string, { x: number; z: number }>();
    if (!isPlaying && playbackProgress === 0) return out;
    for (const a of arrows) {
      if (!a.playerKey) continue;
      const curve = arrowCurves.get(a.id);
      if (!curve) continue;
      const t = Math.min(Math.max(playbackProgress, 0), 1);
      const p = curve.getPointAt(t);
      out.set(a.playerKey, { x: p.x, z: p.z });
    }
    return out;
  }, [arrows, arrowCurves, playbackProgress, isPlaying]);

  // Reduced-motion awareness drives:
  //   - playback RAF loop (skip animation, snap to t=1)
  //   - selection-ring pulse (frozen scale)
  //   - idle bob / arm sway (frozen pose)
  //   - footballer skeletal idle/walk/run (frozen frame)
  // Pulled here at the board level so every child gets the same value via
  // prop — avoids 22 tokens each running their own matchMedia listener.
  const reducedMotion = useReducedMotion();

  // RAF loop while playing — advances progress and pauses on completion so
  // the coach can replay or stop without jumping back to start. When the
  // user has reduce-motion set we honour it by snapping to the end so the
  // scrubber + bound-player end positions still update; we just skip the
  // animation tween between them. Matches the 2D board's behaviour.
  useEffect(() => {
    if (!isPlaying) return;
    if (reducedMotion) {
      setPlaybackProgress(1);
      setIsPlaying(false);
      return;
    }
    let raf = 0;
    const startTs = performance.now() - playbackProgress * PLAYBACK_DURATION_MS;
    const tick = () => {
      const elapsed = performance.now() - startTs;
      const next = Math.min(elapsed / PLAYBACK_DURATION_MS, 1);
      setPlaybackProgress(next);
      if (next >= 1) {
        setIsPlaying(false);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // intentionally not depending on progress — restart bakes start ts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, reducedMotion]);

  const boundArrowCount = useMemo(
    () => arrows.filter((a) => a.playerKey).length,
    [arrows],
  );

  // Debounced save: every meaningful state mutation schedules a write
  // 400ms out. Coach dragging a token doesn't thrash localStorage; the
  // last value in the burst is what lands. No-op if there's no key.
  useEffect(() => {
    if (!persistKey) return;
    const t = setTimeout(() => {
      selfSaveRef.current = true;
      const entry = saveTacticsLayout(persistKey, {
        homeFormation: home,
        awayFormation: away,
        homeSpots,
        awaySpots,
        arrows,
        // Phase 3 — preserve annotations on every write so the surface
        // round-trips zones + coaching points alongside spots/arrows.
        zones,
        coachingPoints,
        // Phase 4 — round-trip roles / team tactics / set pieces /
        // drawings / overlays alongside the rest of the layout. Empty
        // values are still written so Wave A's 2D board sees the same
        // state when it rehydrates from this key.
        playerRoles,
        teamTactics,
        setPieces: setPieces as unknown as TacticsLayout['setPieces'],
        drawings: drawings as unknown as TacticsLayout['drawings'],
        overlays: overlays as unknown as TacticsLayout['overlays'],
        playerBriefs: playerBriefs as unknown as TacticsLayout['playerBriefs'],
        playerBriefsStale,
        playerBriefHistory: playerBriefHistory as unknown as TacticsLayout['playerBriefHistory'],
        keyframes,
        roster,
      });
      setSavedAt(entry.saved_at);
    }, 400);
    return () => clearTimeout(t);
  }, [
    persistKey,
    home,
    away,
    homeSpots,
    awaySpots,
    arrows,
    zones,
    coachingPoints,
    playerRoles,
    teamTactics,
    setPieces,
    drawings,
    overlays,
    playerBriefs,
    playerBriefsStale,
    playerBriefHistory,
    keyframes,
    roster,
  ]);

  // Drag commit — see TacticsBoard2D for the rationale. Zone-crossing
  // drags open `slotPicker` so the coach explicitly picks the new
  // slot; in-zone drags fall through silently.
  // Drag commits position; if it crosses a zone boundary, the slot
  // picker pops up so the coach can confirm or change the role label.
  const moveHomeSpot = useMemo(
    () => (i: number) => (next: { x: number; z: number }) => {
      const activeKey = activeEditKeyframeIdRef.current;
      if (activeKey) {
        setKeyframes((prev) =>
          (prev ?? []).map((k) => {
            if (k.id !== activeKey) return k;
            const homeSpots = k.homeSpots.map((s, idx) => {
              if (idx !== i) return s;
              const clamped = clampToPitch(s, next.x, next.z);
              const inferred = inferRoleAtPosition(clamped.x, clamped.z, 'home');
              if (inferred !== s.role) {
                setSlotPicker({ key: `home-${i}`, currentSlot: s.role, suggested: inferred });
              }
              return clamped;
            });
            return { ...k, homeSpots };
          }),
        );
        return;
      }
      setHomeSpots((prev) =>
        prev.map((s, idx) => {
          if (idx !== i) return s;
          const clamped = clampToPitch(s, next.x, next.z);
          const inferred = inferRoleAtPosition(clamped.x, clamped.z, 'home');
          if (inferred !== s.role) {
            setSlotPicker({ key: `home-${i}`, currentSlot: s.role, suggested: inferred });
          }
          return clamped;
        }),
      );
    },
    [],
  );
  const moveAwaySpot = useMemo(
    () => (i: number) => (next: { x: number; z: number }) => {
      const activeKey = activeEditKeyframeIdRef.current;
      if (activeKey) {
        setKeyframes((prev) =>
          (prev ?? []).map((k) => {
            if (k.id !== activeKey) return k;
            const awaySpots = k.awaySpots.map((s, idx) => {
              if (idx !== i) return s;
              const clamped = clampToPitch(s, next.x, next.z);
              const inferred = inferRoleAtPosition(clamped.x, clamped.z, 'away');
              if (inferred !== s.role) {
                setSlotPicker({ key: `away-${i}`, currentSlot: s.role, suggested: inferred });
              }
              return clamped;
            });
            return { ...k, awaySpots };
          }),
        );
        return;
      }
      setAwaySpots((prev) =>
        prev.map((s, idx) => {
          if (idx !== i) return s;
          const clamped = clampToPitch(s, next.x, next.z);
          const inferred = inferRoleAtPosition(clamped.x, clamped.z, 'away');
          if (inferred !== s.role) {
            setSlotPicker({ key: `away-${i}`, currentSlot: s.role, suggested: inferred });
          }
          return clamped;
        }),
      );
    },
    [],
  );

  const selectView = (next: CameraPresetId) => {
    if (next === view) {
      setSnapNonce((n) => n + 1);
    } else {
      setView(next);
    }
  };

  // Phase 4 — pattern recall. The PatternDrawer fires this with a
  // pattern id; we resolve the full layout via the store and write it
  // through the canonical save path. The existing
  // `onTacticsLayoutChanged` listener above sees the emit and
  // re-hydrates board state, so we don't need to touch local setters
  // here.
  //
  // INTENTIONAL: this clobbers the active session's saved layout. The
  // debounced auto-save (400ms) will rewrite the now-loaded pattern as
  // the board's new state on the next mutation. That is the desired
  // behaviour — "pulling up Tuesday press" replaces what's on the pitch
  // by design. If a coach wants to keep the previous layout they can
  // save it as its own pattern first.
  const handleSelectPattern = useCallback(
    (id: string) => {
      if (!persistKey) {
        setPatternsOpen(false);
        return;
      }
      const pat = getPattern(id);
      if (!pat) {
        setPatternsOpen(false);
        return;
      }
      const { v: _v, saved_at: _savedAt, ...layout } = pat.layout;
      saveTacticsLayout(persistKey, layout);
      setPatternsOpen(false);
    },
    [persistKey],
  );

  // Phase 4 §3 — apply a formation from the library drawer. Standard
  // codes go through `projectFormation` (the same path the chip row
  // uses) so the result lands on the existing spot pipeline; custom
  // ids resolve to world-metre slots stored in localStorage.
  const handleApplyFormation = useCallback(
    (codeOrCustomId: string, team: 'home' | 'away') => {
      // Standard / extended formations: look them up by code.
      if (codeOrCustomId in EXTENDED_FORMATIONS) {
        const slots = EXTENDED_FORMATIONS[codeOrCustomId].slots;
        const spots = (team === 'away' ? mirrorFormationForAway(slots) : slots).map(
          (s) => ({ role: s.role, x: s.x, y: 0.6, z: s.z }),
        );
        formationChangedRef.current = true;
        if (team === 'home') {
          setHome(codeOrCustomId);
          setHomeSpots(spots);
        } else {
          setAway(codeOrCustomId);
          setAwaySpots(spots);
        }
        setFormationsOpen(false);
        return;
      }
      // Seed FORMATIONS handled via existing projection.
      formationChangedRef.current = true;
      if (team === 'home') {
        setHome(codeOrCustomId);
        setHomeSpots(projectFormation(codeOrCustomId, 'home'));
      } else {
        setAway(codeOrCustomId);
        setAwaySpots(projectFormation(codeOrCustomId, 'away'));
      }
      // Custom (coach-saved) — look it up in the store.
      const custom = listCustomFormations().find((cf) => cf.id === codeOrCustomId);
      if (custom) {
        const slots = custom.slots;
        const spots = (team === 'away' ? mirrorFormationForAway(slots) : slots).map(
          (s) => ({ role: s.role, x: s.x, y: 0.6, z: s.z }),
        );
        if (team === 'home') {
          setHome(custom.name);
          setHomeSpots(spots);
        } else {
          setAway(custom.name);
          setAwaySpots(spots);
        }
      }
      setFormationsOpen(false);
    },
    [],
  );

  // Phase 4 §6.5 — applying a set piece snaps the board's tokens +
  // arrows to that play's snapshot. Set pieces don't carry y values
  // (top-down 2D shape) so we inject the token y here.
  const handleApplySetPiece = useCallback((piece: TacticsSetPiece) => {
    const homeNext = (piece.homeSpots ?? []).map((s) => ({
      role: s.role,
      x: s.x,
      y: 0.6,
      z: s.z,
    }));
    const awayNext = (piece.awaySpots ?? []).map((s) => ({
      role: s.role,
      x: s.x,
      y: 0.6,
      z: s.z,
    }));
    setHomeSpots(homeNext);
    setAwaySpots(awayNext);
    setArrows(
      (piece.arrows ?? []).map((a) => ({
        id: a.id,
        points: a.points,
        color: a.color,
        playerKey: a.playerKey,
      })),
    );
    // Frame the set piece as a slice of the field rather than the whole
    // pitch — snap the camera to the third the ball is in. +Z is the
    // home end, −Z the away (attacking) end; the coach can tap any
    // camera preset (e.g. Tactical) to pull back out.
    setView(piece.origin.z >= 0 ? 'defending' : 'attacking');
    setSnapNonce((n) => n + 1);
    // Reset the walkthrough — while it's playing/scrubbed, tokens render
    // keyframe-sampled positions, which would mask the spots we just
    // applied (the coach would see "nothing changed").
    walkthroughResetRef.current();
    setSetPiecesOpen(false);
  }, []);

  // ── /setpieces deep-link: open a saved routine ────────────────────
  // The /setpieces page "Open" action navigates here as
  // `/3d?setpiece=<id>`. On mount we resolve that id via the shared
  // store and snap the board to the piece. Guarded by a ref so it
  // fires exactly once even if the effect body re-runs (StrictMode
  // double-invoke, or a searchParams identity change) — we never want
  // a saved routine to clobber the board twice or after the coach has
  // started editing. Mirrors TacticsBoard2D.
  const setPieceDeepLinkRef = useRef(false);
  useEffect(() => {
    if (setPieceDeepLinkRef.current) return;
    setPieceDeepLinkRef.current = true;
    const id = searchParams.get('setpiece');
    if (!id) return;
    const piece = getSetPiece(id);
    if (piece) handleApplySetPiece(piece);
  }, [searchParams, handleApplySetPiece]);

  // ── /setpieces deep-link: pre-select the drawer tab ───────────────
  // The /setpieces "+ Create" action navigates here as
  // `/3d?openSetPieces=1&type=<SetPieceType>`. `openSetPieces` already
  // seeds `setPiecesOpen`; here we read `type` and — when it is one of
  // the valid SetPieceType values — pass it to the drawer as
  // `initialType` so the matching tab is active on open. An absent or
  // unrecognised value resolves to `undefined` (drawer falls back to
  // its own default tab). Mirrors TacticsBoard2D.
  const initialSetPieceType = useMemo<SetPieceType | undefined>(() => {
    const raw = searchParams.get('type');
    return raw && (SET_PIECE_TYPES as readonly string[]).includes(raw)
      ? (raw as SetPieceType)
      : undefined;
  }, [searchParams]);

  // Set-piece create mode (`?type=corner-att`) — lay out that type's
  // default shape (players in the box, ball at the flag) and snap the
  // camera to it via handleApplySetPiece. Mirrors the 2D board. Fires
  // once; the coach edits from there.
  const setPieceTypeCameraRef = useRef(false);
  useEffect(() => {
    if (setPieceTypeCameraRef.current) return;
    setPieceTypeCameraRef.current = true;
    if (!initialSetPieceType) return;
    // Attacking corners use the conventional-order starter template so
    // players keep their numbers (no position swap); other types fall
    // back to the generic default layout.
    const layout =
      initialSetPieceType === 'corner-att'
        ? CORNER_TEMPLATES[0]
        : defaultSetPiece(initialSetPieceType);
    handleApplySetPiece({
      ...layout,
      id: `draft-${initialSetPieceType}`,
      createdAt: new Date().toISOString(),
    });
    // handleApplySetPiece closes the drawer (right for *applying* a
    // saved routine) — create mode wants it open so the coach can
    // tweak the shape and save via "New from current board".
    setSetPiecesOpen(true);
  }, [initialSetPieceType, handleApplySetPiece]);

  // Phase 4 — undo / redo. The hook subscribes to the store's
  // `tactics-layout-changed` event for our persistKey and tracks
  // snapshots; undo / redo write back via saveTacticsLayout which our
  // rehydrate listener above picks up.
  const history = useTacticsHistory(persistKey);

  // Ctrl/Cmd+Z + Shift+Ctrl/Cmd+Z keyboard shortcuts. Skip when the
  // focus is inside a typeable element (matches the nudge-keys guard).
  useEffect(() => {
    if (!persistKey) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if ((e.key === 'z' || e.key === 'Z') && !e.shiftKey) {
        e.preventDefault();
        history.undo();
      } else if ((e.key === 'z' || e.key === 'Z') && e.shiftKey) {
        e.preventDefault();
        history.redo();
      } else if (e.key === 'y' || e.key === 'Y') {
        e.preventDefault();
        history.redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [persistKey, history]);

  // Phase 2 — walkthrough mode. Mounts only when the persisted layout
  // carries ≥2 keyframes. Hook owns its own progress / RAF so the
  // existing run-playback HUD continues to drive single-arrow runs.
  // `keyframes` is declared earlier (alongside the other early state)
  // so the debounced save effect can reference it.
  const branches = useMemo(
    () => (initial?.branches ?? []) as NonNullable<typeof initial>['branches'],
    [initial],
  );
  const voiceovers = useMemo(
    () => (initial?.voiceovers ?? []) as NonNullable<typeof initial>['voiceovers'],
    [initial],
  );
  const walkthrough = useWalkthroughPlayback({
    keyframes: keyframes ?? [],
    branches: branches,
    voiceovers: voiceovers,
    reducedMotion,
  });
  // Mute / unmute is local — caller passes a speaker only if it wants
  // narration. We default to muted in 3D so nothing speaks unsolicited.
  const [walkthroughTtsEnabled, setWalkthroughTtsEnabled] = useState(false);

  // Sync the active-edit-keyframe ref so the drag handlers declared
  // earlier in the file pick up the latest walkthrough state without
  // needing the walkthrough object in their closure deps. Auto-route
  // drags to the active keyframe when the walkthrough is showing a
  // non-base view (otherwise tokenSpotFor returns the sampled
  // keyframe position but the drag commits to hidden homeSpots).
  const hasKeyframes = (keyframes?.length ?? 0) >= 2;
  useEffect(() => {
    const walkthroughActiveNow =
      hasKeyframes && (walkthrough.isPlaying || walkthrough.progress > 0);
    activeEditKeyframeIdRef.current = walkthroughActiveNow
      ? walkthrough.activeKeyframeId
      : null;
  }, [
    hasKeyframes,
    walkthrough.activeKeyframeId,
    walkthrough.isPlaying,
    walkthrough.progress,
  ]);

  // Keep the walkthrough-reset ref current for handlers declared above
  // the `walkthrough` object (handleApplySetPiece).
  useEffect(() => {
    walkthroughResetRef.current = walkthrough.reset;
  }, [walkthrough.reset]);

  // Walkthrough drives token positions when active. Same shape as the
  // single-arrow `playbackPositions` map; merged below at render time.
  const walkthroughActive =
    (keyframes?.length ?? 0) >= 2 && (walkthrough.isPlaying || walkthrough.progress > 0);

  // Phase 4 §17 — Voronoi cells. Recomputed only when the overlay is
  // active and the spots change; the layer below short-circuits when
  // cells is empty so coaches who never enable Voronoi pay nothing.
  // Wave C analytical overlays. `showAway` is a visual de-clutter
  // toggle for the tokens; the overlays must reflect the actual game
  // state regardless or hiding away would silently change the home
  // analysis. Always feed both teams into the compute path.
  const overlayPoints = useMemo(() => {
    return [
      ...homeSpots.map((s) => ({ x: s.x, z: s.z, team: 'home' as const, role: s.role })),
      ...awaySpots.map((s) => ({ x: s.x, z: s.z, team: 'away' as const, role: s.role })),
    ];
  }, [homeSpots, awaySpots]);

  const voronoiCells = useMemo(() => {
    if (!overlays.active.includes('voronoi')) return [];
    const points =
      voronoiFocus === 'home'
        ? overlayPoints.filter((p) => p.team === 'home')
        : voronoiFocus === 'away'
        ? overlayPoints.filter((p) => p.team === 'away')
        : overlayPoints;
    return computeVoronoiCells(points, FIELD_LENGTH, FIELD_WIDTH);
  }, [overlays.active, overlayPoints, voronoiFocus]);

  const passNetEdges = useMemo(() => {
    if (!overlays.active.includes('passNet')) return [];
    return computePassNetwork(overlayPoints);
  }, [overlays.active, overlayPoints]);

  const teamShapeBands = useMemo(() => {
    if (!overlays.active.includes('teamShape')) return [];
    return computeTeamShapeBands(overlayPoints, FIELD_LENGTH);
  }, [overlays.active, overlayPoints]);

  // Compactness gauges — always-on stats chips.
  const homeCompactness = useMemo(
    () => computeTeamCompactness(homeSpots),
    [homeSpots],
  );
  const awayCompactness = useMemo(
    () => computeTeamCompactness(awaySpots),
    [awaySpots],
  );

  // Heatmap filtered to home — blending two opposing teams' density
  // into one field is nonsense for shape analysis.
  const heatmapGrid = useMemo(() => {
    if (!overlays.active.includes('heatmap')) return null;
    return computeHeatmap(overlayPoints, FIELD_LENGTH, FIELD_WIDTH, {
      team: 'home',
    });
  }, [overlays.active, overlayPoints]);

  const pitchControlGrid = useMemo(() => {
    if (!overlays.active.includes('pitchControl')) return null;
    return computePitchControl(overlayPoints, FIELD_LENGTH, FIELD_WIDTH);
  }, [overlays.active, overlayPoints]);

  const offsideInputs = useMemo(() => {
    return overlayPoints.map((p) => ({
      x: p.x,
      z: p.z,
      team: p.team,
      isGoalkeeper: p.role === 'GK',
    }));
  }, [overlayPoints]);

  const offsideLines = useMemo(() => {
    if (!overlays.active.includes('offsideLine')) {
      return { homeLineZ: null, awayLineZ: null };
    }
    return computeOffsideLines(offsideInputs);
  }, [overlays.active, offsideInputs]);

  const defensiveShapes = useMemo(() => {
    if (!overlays.active.includes('defensiveShape')) {
      return { home: { hull: [] }, away: { hull: [] } };
    }
    return {
      home: computeDefensiveShape(offsideInputs, 'home'),
      away: computeDefensiveShape(offsideInputs, 'away'),
    };
  }, [overlays.active, offsideInputs]);

  // Slot lookup helper for the role picker — derives the formation
  // slot of a clicked token from its `team-index` key.
  const slotForKey = useCallback(
    (key: string): string => {
      const [side, idxStr] = key.split('-');
      const idx = Number(idxStr);
      const spots = side === 'home' ? homeSpots : awaySpots;
      return spots[idx]?.role ?? 'CM';
    },
    [homeSpots, awaySpots],
  );

  // Pointer capture for drawing primitives (§7). World coords come
  // directly from the R3F `<DrawingCaptureMesh>` (event.point on a
  // floor-plane raycast hit), so no SVG / CTM math is needed.
  // Each drawing kind has its own commit shape (lines/arrows = 2
  // points, freehand = sampled polyline, text/rect/ellipse = 2 corners).
  const drawingStartRef = useRef<{ x: number; z: number } | null>(null);
  const drawingPointsRef = useRef<Array<{ x: number; z: number }>>([]);
  const [drawingPreview, setDrawingPreview] = useState<TacticsDrawing | null>(null);
  // Zone tool — tap-corners + double-tap to close. Separate refs from
  // the drag-based capture above because the polygon's in-progress
  // state spans multiple pointer cycles (one cycle per corner). See
  // TacticsBoard2D for the mirrored implementation.
  const polygonPtsRef = useRef<Array<{ x: number; z: number }>>([]);
  const polygonLastTapRef = useRef<{ ts: number; x: number; z: number } | null>(
    null,
  );
  const polygonDownPosRef = useRef<{ x: number; z: number } | null>(null);
  // State mirror of the ref + hover position — drives the
  // ZonePolygonOverlay render (refs alone don't trigger re-render).
  const [polygonCorners, setPolygonCorners] = useState<
    Array<{ x: number; z: number }>
  >([]);
  const [zoneHover, setZoneHover] = useState<{ x: number; z: number } | null>(
    null,
  );
  const ZONE_TAP_TOLERANCE_M = 1.5;
  const ZONE_DOUBLE_TAP_MS = 500;
  const ZONE_DOUBLE_TAP_TOLERANCE_M = 2.0;

  // Discard in-progress polygon when the coach leaves zone mode so the
  // next zone session starts with no stale corners (mirrors 2D).
  useEffect(() => {
    if (drawingActiveKind !== 'zone') {
      polygonPtsRef.current = [];
      polygonLastTapRef.current = null;
      polygonDownPosRef.current = null;
      setPolygonCorners([]);
      setZoneHover(null);
      setDrawingPreview((p) => (p?.kind === 'zone' ? null : p));
    }
  }, [drawingActiveKind]);

  // Esc cancels the in-progress polygon, Backspace pops the last
  // corner — same UX as 2D so the tool feels identical on both
  // surfaces.
  useEffect(() => {
    if (drawingActiveKind !== 'zone') return undefined;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'Escape') {
        if (polygonPtsRef.current.length === 0) return;
        e.preventDefault();
        polygonPtsRef.current = [];
        polygonLastTapRef.current = null;
        setPolygonCorners([]);
        setDrawingPreview(null);
        return;
      }
      if (e.key === 'Backspace') {
        if (polygonPtsRef.current.length === 0) return;
        e.preventDefault();
        const next = polygonPtsRef.current.slice(0, -1);
        polygonPtsRef.current = next;
        polygonLastTapRef.current = null;
        setPolygonCorners(next);
        if (next.length === 0) {
          setDrawingPreview(null);
        } else {
          const base = defaultDrawing('zone');
          setDrawingPreview({
            ...base,
            id: 'preview',
            kind: 'zone',
            points: next,
            stroke: drawingStroke,
            strokeWidth: clampStrokeWidth(drawingStrokeWidth),
            dash: drawingDash,
          });
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    drawingActiveKind,
    drawingStroke,
    drawingStrokeWidth,
    drawingDash,
  ]);

  const commitDrawing = useCallback(
    (points: Array<{ x: number; z: number }>) => {
      if (!drawingActiveKind) return;
      if (points.length < 1) return;
      if (drawingActiveKind !== 'text' && points.length < 2) return;
      const id = `drawing-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const base = defaultDrawing(drawingActiveKind);
      const entry: TacticsDrawing = {
        ...base,
        id,
        points,
        stroke: drawingStroke,
        strokeWidth: clampStrokeWidth(drawingStrokeWidth),
        dash: drawingDash,
        ...(drawingActiveKind === 'arrow' || drawingActiveKind === 'curved-arrow'
          ? { arrowHead: drawingArrowHead }
          : {}),
      };
      setDrawings((prev) => [...prev, entry]);
    },
    [drawingActiveKind, drawingStroke, drawingStrokeWidth, drawingDash, drawingArrowHead],
  );

  // ── Drawing capture callbacks (DrawingCaptureMesh feeds world {x, z}) ──
  // Same state machine the SVG capture used to drive, but the source
  // of the world coords is now an R3F raycaster intersection on a
  // transparent floor plane instead of an SVG-CTM inverse projection.
  //
  // Branches by kind:
  //   - text: prompt + commit on the spot
  //   - zone: down records press position; up runs tap/double-tap
  //   - everything else: drag-based capture with live preview
  const handleCapturePointerDown = useCallback(
    (world: { x: number; z: number }, _pointerId: number) => {
      if (!drawingActiveKind) return;
      if (drawingActiveKind === 'zone') {
        polygonDownPosRef.current = world;
        return;
      }
      drawingStartRef.current = world;
      drawingPointsRef.current = [world];
      if (drawingActiveKind === 'text') {
        const text =
          typeof window !== 'undefined' && typeof window.prompt === 'function'
            ? window.prompt('Label text')
            : null;
        if (text) {
          const id = `drawing-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
          const base = defaultDrawing('text');
          setDrawings((prev) => [
            ...prev,
            { ...base, id, points: [world], stroke: drawingStroke, text },
          ]);
        }
        drawingStartRef.current = null;
        drawingPointsRef.current = [];
        return;
      }
      setDrawingPreview({
        ...(defaultDrawing(drawingActiveKind) as Omit<TacticsDrawing, 'id' | 'points'>),
        id: 'preview',
        points: [world],
        stroke: drawingStroke,
        strokeWidth: clampStrokeWidth(drawingStrokeWidth),
        dash: drawingDash,
        ...(drawingActiveKind === 'arrow' || drawingActiveKind === 'curved-arrow'
          ? { arrowHead: drawingArrowHead }
          : {}),
      });
    },
    [
      drawingActiveKind,
      drawingStroke,
      drawingStrokeWidth,
      drawingDash,
      drawingArrowHead,
    ],
  );

  const handleCapturePointerMove = useCallback(
    (world: { x: number; z: number }) => {
      if (drawingActiveKind === 'zone') {
        setZoneHover(world);
        return;
      }
      if (!drawingActiveKind || !drawingStartRef.current) return;
      if (drawingActiveKind === 'freehand') {
        const last = drawingPointsRef.current[drawingPointsRef.current.length - 1]!;
        if (Math.hypot(world.x - last.x, world.z - last.z) < 0.5) return;
        drawingPointsRef.current.push(world);
        setDrawingPreview((prev) =>
          prev ? { ...prev, points: [...drawingPointsRef.current] } : prev,
        );
        return;
      }
      // 2-point shapes (line / arrow / rect / ellipse): start + current.
      drawingPointsRef.current = [drawingStartRef.current, world];
      setDrawingPreview((prev) =>
        prev ? { ...prev, points: [drawingStartRef.current!, world] } : prev,
      );
    },
    [drawingActiveKind],
  );

  const handleCapturePointerUp = useCallback(
    (world: { x: number; z: number }, _pointerId: number) => {
      if (drawingActiveKind === 'zone') {
        const down = polygonDownPosRef.current;
        polygonDownPosRef.current = null;
        if (!down) return;
        const moved = Math.hypot(world.x - down.x, world.z - down.z);
        if (moved > ZONE_TAP_TOLERANCE_M) return;
        const now = Date.now();
        const last = polygonLastTapRef.current;
        const isDouble =
          last !== null &&
          now - last.ts <= ZONE_DOUBLE_TAP_MS &&
          Math.hypot(world.x - last.x, world.z - last.z) <=
            ZONE_DOUBLE_TAP_TOLERANCE_M;
        if (isDouble) {
          const corners = polygonPtsRef.current;
          polygonPtsRef.current = [];
          polygonLastTapRef.current = null;
          setPolygonCorners([]);
          setDrawingPreview(null);
          if (corners.length >= 3) commitDrawing(corners);
          return;
        }
        const nextCorners = [...polygonPtsRef.current, world];
        polygonPtsRef.current = nextCorners;
        polygonLastTapRef.current = { ts: now, x: world.x, z: world.z };
        setPolygonCorners(nextCorners);
        const base = defaultDrawing('zone');
        setDrawingPreview({
          ...base,
          id: 'preview',
          kind: 'zone',
          points: nextCorners,
          stroke: drawingStroke,
          strokeWidth: clampStrokeWidth(drawingStrokeWidth),
          dash: drawingDash,
        });
        return;
      }
      if (!drawingActiveKind || !drawingStartRef.current) return;
      const points = drawingPointsRef.current.slice();
      drawingStartRef.current = null;
      drawingPointsRef.current = [];
      setDrawingPreview(null);
      commitDrawing(points);
    },
    [
      drawingActiveKind,
      drawingStroke,
      drawingStrokeWidth,
      drawingDash,
      commitDrawing,
    ],
  );

  const handleCapturePointerLeave = useCallback(() => {
    if (drawingActiveKind === 'zone') setZoneHover(null);
  }, [drawingActiveKind]);

  // Click on empty pitch / canvas background clears selection.
  const clearSelection = () => setSelectedKey(null);

  // Keyboard a11y: Escape clears selection, arrow keys nudge the selected
  // token by NUDGE_DISTANCE metres. Lets coaches refine positions without
  // reaching for the mouse — mirrors the tactical-board feel of FIFA's
  // pause-menu nudge controls.
  const NUDGE_DISTANCE = 1; // metres per keypress
  useEffect(() => {
    if (!selectedKey) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedKey(null);
        return;
      }
      const dx = e.key === 'ArrowLeft' ? -NUDGE_DISTANCE : e.key === 'ArrowRight' ? NUDGE_DISTANCE : 0;
      const dz = e.key === 'ArrowUp' ? -NUDGE_DISTANCE : e.key === 'ArrowDown' ? NUDGE_DISTANCE : 0;
      if (dx === 0 && dz === 0) return;
      // Don't fight form fields if the focus is somewhere typeable.
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      e.preventDefault();
      const [side, idxStr] = selectedKey.split('-');
      const idx = Number(idxStr);
      // Arrow nudges only move 1 m at a time so we don't auto-retag
      // from a nudge — that would spam the picker. Only the explicit
      // drag commit opens the picker on zone change.
      if (side === 'home') {
        setHomeSpots((prev) =>
          prev.map((s, i) => (i === idx ? clampToPitch(s, s.x + dx, s.z + dz) : s)),
        );
      } else if (side === 'away') {
        setAwaySpots((prev) =>
          prev.map((s, i) => (i === idx ? clampToPitch(s, s.x + dx, s.z + dz) : s)),
        );
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedKey]);

  // WebGL probe runs once on mount. If the browser/tab can't give us a
  // GL context the R3F <Canvas> below will mount but render nothing
  // visible — which is worse UX than a plain explanation + a link to
  // the 2D fallback. Probe synchronously so we don't flash an empty box
  // on the first paint.
  const webglAvailable = useMemo(detectWebGLSupport, []);

  // Recover the session id from the persistKey (format `tactics-3d-{id}`)
  // so the WebGL-unavailable fallback can deep-link to the same session's
  // 2D route. If there's no id (ephemeral board) we drop the user on the
  // sessions list instead.
  const fallback2dHref = useMemo(() => {
    if (!persistKey) return '/sessions';
    const prefix = 'tactics-3d-';
    if (persistKey.startsWith(prefix)) {
      const id = persistKey.slice(prefix.length);
      if (id && id !== 'default') return `/sessions/${id}/2d`;
    }
    return '/sessions';
  }, [persistKey]);

  if (!webglAvailable) {
    return (
      <div
        className="t3d-root t3d-root--fallback tactics-stage"
        data-testid="tactics-board-3d"
        role="region"
        aria-label="3D tactics board"
      >
        <div className="t3d-fallback">
          <p className="t3d-fallback__title">3D tactics board unavailable</p>
          <p className="t3d-fallback__body">
            Your browser couldn&apos;t start a 3D graphics context. This usually
            happens on older hardware or when hardware acceleration is turned
            off. You can use the 2D board instead — it has the same formations,
            drag, and arrow drawing.
          </p>
          <a className="t3d-fallback__link" href={fallback2dHref}>
            Open the 2D tactics board
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className="t3d-root tactics-stage"
      data-testid="tactics-board-3d"
      role="region"
      aria-label="3D tactics board"
    >
      {/* Top-right tactic actions menu — mirror of the 2D board. */}
      <TacticMenu
        onCopy={async () => {
          const snap = {
            v: 5 as const,
            homeFormation: home,
            awayFormation: away,
            homeSpots,
            awaySpots,
            arrows,
            zones,
            coachingPoints,
            drawings: drawings as unknown as TacticsLayoutDrawings[],
            saved_at: savedAt ?? new Date().toISOString(),
          };
          const code = encodeTacticCode(snap);
          try {
            await navigator.clipboard?.writeText(code);
          } catch {
            /* clipboard denied — silent */
          }
        }}
        onPaste={async () => {
          let code: string | null = null;
          try {
            code = (await navigator.clipboard?.readText()) ?? null;
          } catch {
            code = null;
          }
          if (!code) return;
          const parsed = decodeTacticCode(code);
          if (!parsed) return;
          setHome(parsed.homeFormation);
          setAway(parsed.awayFormation);
          setHomeSpots(parsed.homeSpots);
          setAwaySpots(parsed.awaySpots);
          setArrows(parsed.arrows);
          if (parsed.zones) setZones(parsed.zones);
          if (parsed.coachingPoints) setCoachingPoints(parsed.coachingPoints);
          if (parsed.drawings) {
            setDrawings(parsed.drawings as unknown as TacticsDrawing[]);
          }
        }}
        onSave={(name) => {
          savePattern({
            name,
            phaseOfPlay: 'attacking',
            formation: home,
            layout: {
              v: 5,
              homeFormation: home,
              awayFormation: away,
              homeSpots,
              awaySpots,
              arrows,
              zones,
              coachingPoints,
              playerRoles,
              teamTactics,
              setPieces: setPieces as unknown as TacticsLayout['setPieces'],
              drawings: drawings as unknown as TacticsLayout['drawings'],
              keyframes,
              saved_at: savedAt ?? new Date().toISOString(),
            },
          });
        }}
        onLoad={() => setPatternsOpen(true)}
      />
      {/* Flex-fill wrapper so the Canvas occupies the pitch slot above
          the HUD without overlapping it. Matches the 2D board's
          `.t2d-stage` role under `.tactics-stage`. */}
      <div className="tactics-stage__board">
      <Canvas
        // PCFSoftShadowMap softens shadow edges so the directional rig
        // doesn't produce hard pixelated boundaries on player silhouettes.
        shadows={{ type: THREE.PCFSoftShadowMap }}
        camera={{ position: [0, 55, 60], fov: 32, near: 1, far: 600 }}
        gl={{
          antialias: true,
          alpha: true,
          // ACES Filmic gives the cinematic colour grade — crushed blacks,
          // soft highlight rolloff, no clipping. The default Linear tone
          // map looks "video game" instead of "broadcast TV".
          toneMapping: THREE.ACESFilmicToneMapping,
          // Mild exposure lift — the HDRI environment alone is on the
          // dim side because of fog + dark surfaces.
          toneMappingExposure: 1.05,
        }}
        dpr={[1, 2]}
      >
        <color attach="background" args={['#050d0a']} />
        {/* Fog far pushed from 260 → 520 so the far half of the 105m pitch
            doesn't fade into the background colour at the default camera
            distance. Near 140 → 180 softens the onset. */}
        <fog attach="fog" args={['#050d0a', 180, 520]} />
        <Lighting />
        <Suspense fallback={null}>
          {/* HDRI environment — image-based lighting drives skin/cloth/leather
              specular response. Switched 'studio' → 'sunset': stadium
              evening match feel; warmer falloff that PBR jerseys + skin
              read against as 3D rather than as cardboard cutouts. */}
          <Environment preset="sunset" background={false} environmentIntensity={0.85} />
          {!hideField && <FieldMesh />}
          {/* Analytical Spacing overlay — rendered as flat geometry on
              the pitch (y=0.005) so the 3D camera projects it with
              real perspective. Previously this was a 2D SVG plastered
              over the canvas with no perspective correction; that read
              as "2D copied onto 3D" and didn't align under any non-top-
              down camera preset. */}
          {overlays.active.includes('voronoi') && voronoiCells.length > 0 && (
            <VoronoiLayer3D
              cells={voronoiCells}
              opacity={overlays.opacity.voronoi}
              showAway={voronoiFocus !== 'both' ? true : showAway}
            />
          )}
          {overlays.active.includes('halfSpaces') && (
            <HalfSpacesLayer3D opacity={overlays.opacity.halfSpaces} />
          )}
          {overlays.active.includes('posPlayGrid') && (
            <PositionalPlayGridLayer3D opacity={overlays.opacity.posPlayGrid} />
          )}
          {overlays.active.includes('xt') && (
            <XTLayer3D opacity={overlays.opacity.xt} />
          )}
          {overlays.active.includes('teamShape') && (
            <TeamShapeLayer3D
              bands={teamShapeBands}
              opacity={overlays.opacity.teamShape}
              showAway={showAway}
            />
          )}
          {overlays.active.includes('heatmap') && heatmapGrid && (
            <HeatmapLayer3D
              grid={heatmapGrid}
              opacity={overlays.opacity.heatmap}
            />
          )}
          {overlays.active.includes('pitchControl') && pitchControlGrid && (
            <PitchControlLayer3D
              grid={pitchControlGrid}
              opacity={overlays.opacity.pitchControl}
              showAway={showAway}
            />
          )}
          {overlays.active.includes('passNet') && (
            <PassNetLayer3D
              edges={passNetEdges}
              opacity={overlays.opacity.passNet}
            />
          )}
          {overlays.active.includes('defensiveShape') && (
            <DefensiveShapeLayer3D
              homeHull={defensiveShapes.home}
              awayHull={defensiveShapes.away}
              opacity={overlays.opacity.defensiveShape}
            />
          )}
          {overlays.active.includes('offsideLine') && (
            <OffsideLineLayer3D
              homeLineZ={offsideLines.homeLineZ}
              awayLineZ={offsideLines.awayLineZ}
              opacity={overlays.opacity.offsideLine}
            />
          )}
          {/* Phase 3 zone layer — sits at y=0.005, just above the grass
              and below the player tokens (y=0.6+). ZoneLayer3D returns
              null when there are no zones so v=1 layouts pay nothing. */}
          <ZoneLayer3D zones={zones} />
          {/* Home goal at +z (own end), away goal at -z, opening into the field. */}
          <Goal position={[0, 0, FIELD_LENGTH / 2]} facing="south" />
          <Goal position={[0, 0, -FIELD_LENGTH / 2]} facing="north" />
          {/* Empty-pitch click target: a flat invisible plane just above
              the grass that catches background clicks to clear selection. */}
          <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, 0.0001, 0]}
            onPointerDown={clearSelection}
          >
            <planeGeometry args={[FIELD_WIDTH, FIELD_LENGTH]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
          {homeSpots.map((spot, i) => {
            const key = `home-${i}`;
            return (
              <PlayerToken3D
                key={key}
                spot={spot}
                number={i + 1}
                team="home"
                selected={selectedKey === key}
                playbackPos={
                  walkthroughActive && walkthrough.positions.has(key)
                    ? (() => {
                        const w = walkthrough.positions.get(key)!;
                        return { x: w.x, z: w.z };
                      })()
                    : playbackPositions.get(key) ?? null
                }
                gait={
                  walkthroughActive && walkthrough.positions.has(key)
                    ? (walkthrough.isPlaying ? 'walk' : 'walk')
                    : playbackPositions.has(key)
                      ? (isPlaying ? 'run' : 'walk') // auto-play sprints; manual scrub walks
                      : 'idle'
                }
                reducedMotion={reducedMotion}
                onSelect={() => {
                  if (roleMode) {
                    setRoleTarget({ key, slot: slotForKey(key) });
                  } else {
                    setSelectedKey(key);
                  }
                }}
                onDragChange={(dragging) => setOrbitEnabled(!dragging)}
                onMove={moveHomeSpot(i)}
                mode={mode}
                onStartArrowFromToken={(origin) => setArrowSeed({ origin, playerKey: key })}
              />
            );
          })}
          {showAway && awaySpots.map((spot, i) => {
            const key = `away-${i}`;
            return (
              <PlayerToken3D
                key={key}
                spot={spot}
                number={i + 1}
                team="away"
                selected={selectedKey === key}
                playbackPos={
                  walkthroughActive && walkthrough.positions.has(key)
                    ? (() => {
                        const w = walkthrough.positions.get(key)!;
                        return { x: w.x, z: w.z };
                      })()
                    : playbackPositions.get(key) ?? null
                }
                gait={
                  walkthroughActive && walkthrough.positions.has(key)
                    ? (walkthrough.isPlaying ? 'walk' : 'walk')
                    : playbackPositions.has(key)
                      ? (isPlaying ? 'run' : 'walk') // auto-play sprints; manual scrub walks
                      : 'idle'
                }
                reducedMotion={reducedMotion}
                onSelect={() => {
                  if (roleMode) {
                    setRoleTarget({ key, slot: slotForKey(key) });
                  } else {
                    setSelectedKey(key);
                  }
                }}
                onDragChange={(dragging) => setOrbitEnabled(!dragging)}
                onMove={moveAwaySpot(i)}
                mode={mode}
                onStartArrowFromToken={(origin) => setArrowSeed({ origin, playerKey: key })}
              />
            );
          })}
          <BallMesh />
          {arrows.map((a, i) => (
            <MovementArrow3D key={a.id} arrow={a} yOffset={i * 0.02} />
          ))}
          {/* Coaching-point pins (Phase 3). No active keyframe wired yet
              — Agent D owns the keyframe UI — so we show only the pins
              without a `keyframeId` (general teaching points). Pins
              bound to a keyframe stay hidden until playback engages. */}
          {coachingPoints
            .filter((p) => !p.keyframeId)
            .map((p) => (
              <CoachingPointPin key={p.id} point={p} surface="r3f" />
            ))}
          {/* Soft global contact shadows under everything — way more
              realistic than a per-player flat disc. The shadow plane
              sits slightly above the grass to avoid z-fighting and
              opacity 0.4 keeps the pitch markings legible underneath. */}
          <ContactShadows
            position={[0, 0.01, 0]}
            opacity={0.45}
            scale={130}
            blur={2.4}
            far={10}
            resolution={1024}
            color="#000000"
          />
          <ArrowDrawLayer
            active={mode === 'draw'}
            color={arrowColorHex}
            seedOrigin={arrowSeed?.origin ?? null}
            onSeedConsumed={() => setArrowSeed(null)}
            onArrowComplete={(points) => {
              const id = `arrow-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
              // If the draw started from a token, bind the arrow to that
              // playerKey so playback animates them along the curve.
              const playerKey = arrowSeed?.playerKey ?? undefined;
              setArrows((prev) => [...prev, { id, points, color: arrowColorHex, playerKey }]);
            }}
            onDragChange={(dragging) => setOrbitEnabled(!dragging)}
          />
          {/* Coach-authored drawings — rendered as R3F geometry so
              every shape projects onto the pitch with real perspective
              (used to be a 2D SVG layer plastered over the canvas). */}
          <DrawingLayer3D drawings={drawings} />
          {drawingPreview && <DrawingLayer3D drawings={[drawingPreview]} />}
          {drawingActiveKind === 'zone' &&
            (polygonCorners.length > 0 || zoneHover !== null) && (
              <ZonePolygonOverlay3D
                corners={polygonCorners}
                hover={zoneHover}
                stroke={drawingStroke}
                strokeWidth={clampStrokeWidth(drawingStrokeWidth)}
              />
            )}
          {/* Drawing capture surface — transparent floor mesh that
              receives pointer events in world coords (no CTM math).
              Only mounted while a tool is active so OrbitControls +
              token drag aren't intercepted. */}
          <DrawingCaptureMesh
            activeKind={drawingActiveKind}
            pitchLengthM={FIELD_LENGTH}
            pitchWidthM={FIELD_WIDTH}
            onPointerDown={handleCapturePointerDown}
            onPointerMove={handleCapturePointerMove}
            onPointerUp={handleCapturePointerUp}
            onPointerLeave={handleCapturePointerLeave}
          />
        </Suspense>
        <OrbitControls
          ref={controlsRef}
          enabled={orbitEnabled}
          enablePan={false}
          minDistance={45}
          maxDistance={140}
          minPolarAngle={Math.PI / 8}
          maxPolarAngle={Math.PI / 2.05}
          target={[0, 0, 0]}
        />
        <CameraRig key={snapNonce} preset={view} controlsRef={controlsRef} />
        {/* Drill-specific overlay slot — mounted inside the same Canvas
            so it shares lighting + camera. Used by the drill canvas to
            render rolePlayers / cones / animated content. */}
        {children}
      </Canvas>
      {/* The 2D SVG overlay that used to sit above the canvas is
          gone — every drawing, analytical overlay, and the zone-tool
          live preview now renders as R3F geometry inside the Canvas.
          Capture happens via <DrawingCaptureMesh>, a transparent
          floor plane that receives pointer events in world coords. */}
      </div>

      {!hideHud && <HudShell>
        {/* Sister-surface nav — jump to Session / Set Pieces / Match Day. */}
        <SurfacePills />
        <HudRow
          label="Mode"
          trailing={persistKey ? <SavedAgo savedAt={savedAt} /> : undefined}
        >
          <HudChip active={mode === 'move'} onClick={() => setMode('move')}>
            Move
          </HudChip>
          <HudChip active={mode === 'draw'} onClick={() => setMode('draw')}>
            Draw arrow
          </HudChip>
          {mode === 'draw' && (
            <>
              <HudHint>
                drag from a player to bind a run · drag on grass for a free arrow
              </HudHint>
              <span className="thud__label" style={{ marginLeft: 8 }}>Colour</span>
              {(['home', 'away', 'neutral'] as const).map((c) => (
                <HudChip
                  key={c}
                  team={c === 'away' ? 'away' : undefined}
                  active={arrowColor === c}
                  onClick={() => setArrowColor(c)}
                >
                  {c}
                </HudChip>
              ))}
              {arrows.length > 0 && (
                <>
                  <HudChip onClick={() => setArrows((prev) => prev.slice(0, -1))}>
                    Undo
                  </HudChip>
                  <HudChip onClick={() => setArrows([])}>Clear arrows</HudChip>
                </>
              )}
            </>
          )}
          {mode === 'move' && (
            <>
              <HudHint>tap a token + drag to reposition</HudHint>
              <HudChip
                onClick={() => {
                  setHomeSpots(projectFormation(home, 'home'));
                  setAwaySpots(projectFormation(away, 'away'));
                  setArrows([]);
                  setZones([]);
                  setCoachingPoints([]);
                  // Phase 4 — wipe roles / set pieces / drawings on
                  // reset too. Overlays stay so the coach's toggle
                  // preferences survive a token-layout reset.
                  setPlayerRoles({});
                  setSetPieces([]);
                  setDrawings([]);
                  setSelectedKey(null);
                  if (persistKey) {
                    clearTacticsLayout(persistKey);
                    setSavedAt(null);
                  }
                }}
              >
                Reset
              </HudChip>
            </>
          )}
        </HudRow>
        <HudRow label="View">
          {CAMERA_PRESETS.map((preset) => (
            <HudChip
              key={preset.id}
              active={view === preset.id}
              onClick={() => selectView(preset.id)}
            >
              {preset.label}
            </HudChip>
          ))}
          <HudChip
            active={showAway}
            ariaLabel={showAway ? 'Hide opponent team' : 'Show opponent team'}
            onClick={() => setShowAway((s) => !s)}
          >
            {showAway ? 'Opponent · on' : 'Opponent · off'}
          </HudChip>
          {/* Patterns chip moved into the Design row's Library
              dropdown — see <LibraryMenu /> below. */}
        </HudRow>

        {/* Compactness gauges — mirrors the 2D board's "Stats" row. */}
        <HudRow label="Stats">
          <CompactnessChips
            home={homeCompactness}
            away={awayCompactness}
            showAway={showAway}
          />
        </HudRow>

        {/* Phase 4 — Design row. Surfaces the new HUDs alongside
            persistKey-scoped undo / redo. We keep the row visible
            even when the board is ephemeral so coaches see the
            toolbar; the undo / redo chips disable themselves when
            there's no history. */}
        <HudRow label="Design">
          <LibraryMenu
            anyOpen={tacticsPanelOpen === 'home' || setPiecesOpen || patternsOpen}
            onOpenPatterns={() => setPatternsOpen(true)}
            onOpenTeamTactics={() => {
              if (!teamTactics) setTeamTactics(defaultBundle());
              setTacticsPanelOpen('home');
            }}
            onOpenSetPieces={() => navigate('/setpieces')}
          />
          <HudChip
            active={overlaysOpen}
            ariaLabel="Toggle analytical overlays"
            onClick={() => setOverlaysOpen((o) => !o)}
          >
            Overlays
          </HudChip>
          <HudChip
            active={briefsOpen}
            ariaLabel="Open player briefs"
            onClick={() => setBriefsOpen((o) => !o)}
          >
            Briefs{playerBriefsStale && Object.keys(playerBriefs).length > 0 ? ' •' : ''}
          </HudChip>
          {/* Design / Phase + + Keyframe chips removed — see the
              matching note on TacticsBoard2D. State retained so
              Brain Bar's sequence-commit path still works. */}
          {/* Copy / Paste code chips moved to the top-right TacticMenu
              alongside Save / Load to library. */}
          <HudChip
            ariaLabel="Mirror layout left to right"
            onClick={() => {
              // Snapshot the coordinate state, mirror across x, push
              // back via setters. Permissive `TacticsLayout.drawings`
              // bridged via `unknown` to the concrete board type.
              const snap = {
                v: 4 as const,
                homeFormation: home,
                awayFormation: away,
                homeSpots,
                awaySpots,
                arrows,
                zones,
                coachingPoints,
                drawings: drawings as unknown as TacticsLayoutDrawings[],
                saved_at: savedAt ?? new Date().toISOString(),
              };
              const flipped = mirrorTacticsLayout(snap, 'x');
              setHomeSpots(flipped.homeSpots);
              setAwaySpots(flipped.awaySpots);
              setArrows(flipped.arrows);
              if (flipped.zones) setZones(flipped.zones);
              if (flipped.coachingPoints) setCoachingPoints(flipped.coachingPoints);
              if (flipped.drawings) {
                setDrawings(flipped.drawings as unknown as TacticsDrawing[]);
              }
            }}
          >
            Mirror
          </HudChip>
          {/* "Roles" chip removed per coach feedback — see the matching
              note on TacticsBoard2D. Open-play role assignment was
              clutter; named-role data still flows through
              layout.playerRoles via Brain Bar and famous tactics. */}
          <HudChip
            active={drawingActiveKind !== null}
            ariaLabel={drawingActiveKind ? 'Exit draw tool' : 'Enter draw tool'}
            onClick={() =>
              setDrawingActiveKind((k) => (k === null ? 'arrow' : null))
            }
          >
            Draw
          </HudChip>
          {drawings.length > 0 && (
            <HudChip
              ariaLabel="Clear all drawings"
              onClick={() => setDrawings([])}
            >
              Clear drawings
            </HudChip>
          )}
          {/* Undo / Redo: plain `<button disabled>` when there's no
              history so the visual disabled state lines up with the
              actual no-op behaviour (HudChip doesn't carry a disabled
              prop — see `HudChip.tsx`). */}
          {history.canUndo ? (
            <HudChip ariaLabel="Undo last change" onClick={() => history.undo()}>
              Undo
            </HudChip>
          ) : (
            <button
              type="button"
              className="thud__chip"
              disabled
              aria-label="Undo last change"
              aria-disabled="true"
            >
              Undo
            </button>
          )}
          {history.canRedo ? (
            <HudChip ariaLabel="Redo last change" onClick={() => history.redo()}>
              Redo
            </HudChip>
          ) : (
            <button
              type="button"
              className="thud__chip"
              disabled
              aria-label="Redo last change"
              aria-disabled="true"
            >
              Redo
            </button>
          )}
        </HudRow>

        {/* Drawing toolbar — mounts only when a kind is active so the
            HUD stays compact in default move mode. */}
        {drawingActiveKind !== null && (
          <DrawingToolbar
            activeKind={drawingActiveKind}
            onSelectKind={setDrawingActiveKind}
            stroke={drawingStroke}
            onStrokeChange={setDrawingStroke}
            strokeWidth={drawingStrokeWidth}
            onStrokeWidthChange={setDrawingStrokeWidth}
            dash={drawingDash}
            onDashChange={setDrawingDash}
            arrowHead={drawingArrowHead}
            onArrowHeadChange={setDrawingArrowHead}
          />
        )}

        {/* Overlay toggles — inline strip surfaces under the Design
            row once the coach opens the Overlays chip. */}
        {overlaysOpen && (
          <HudRow label="Overlays">
            <OverlayToggles state={overlays} onChange={setOverlays} />
            {overlays.active.includes('voronoi') && (
              <>
                <HudChip
                  active={voronoiFocus === 'both'}
                  ariaLabel="Spacing focus: both teams"
                  onClick={() => setVoronoiFocus('both')}
                >
                  Both
                </HudChip>
                <HudChip
                  active={voronoiFocus === 'home'}
                  ariaLabel="Spacing focus: home only"
                  onClick={() => setVoronoiFocus('home')}
                  team="home"
                >
                  Home only
                </HudChip>
                <HudChip
                  active={voronoiFocus === 'away'}
                  ariaLabel="Spacing focus: away only"
                  onClick={() => setVoronoiFocus('away')}
                  team="away"
                >
                  Away only
                </HudChip>
              </>
            )}
          </HudRow>
        )}

        {/* Walkthrough — keyframe playback HUD. Renders nothing for
            single-snapshot layouts (the existing run-playback row
            below covers single-arrow scrubbing). */}
        {(keyframes?.length ?? 0) >= 2 && (
          <HudRow label="Walk">
            <WalkthroughControls
              keyframes={keyframes ?? []}
              branches={branches}
              voiceovers={voiceovers}
              isPlaying={walkthrough.isPlaying}
              progress={walkthrough.progress}
              activeKeyframeId={walkthrough.activeKeyframeId}
              activeBranchId={walkthrough.activeBranchId}
              onPlay={walkthrough.play}
              onPause={walkthrough.pause}
              onReset={walkthrough.reset}
              onScrub={walkthrough.setProgress}
              onSelectBranch={walkthrough.setBranch}
              ttsEnabled={walkthroughTtsEnabled}
              onToggleTts={() => setWalkthroughTtsEnabled((e) => !e)}
            />
          </HudRow>
        )}

        {boundArrowCount > 0 && (
          <HudRow label="Runs">
            <RunPlaybackControls
              isPlaying={isPlaying}
              progress={playbackProgress}
              boundArrowCount={boundArrowCount}
              onPlayPause={() => {
                if (isPlaying) {
                  setIsPlaying(false);
                  return;
                }
                if (playbackProgress >= 1) setPlaybackProgress(0);
                setIsPlaying(true);
              }}
              onStop={() => {
                setIsPlaying(false);
                setPlaybackProgress(0);
              }}
              onScrub={(next) => {
                setIsPlaying(false);
                setPlaybackProgress(next);
              }}
            />
          </HudRow>
        )}
        <HudRow label="Home">
          <FormationDropdown
            current={home}
            customs={listCustomFormations()}
            onChange={(codeOrId) => handleApplyFormation(codeOrId, 'home')}
            onSaveCustom={(name) => {
              saveCustomFormation({
                name,
                slots: homeSpots.map((s) => ({ role: s.role, x: s.x, z: s.z })),
              });
            }}
          />
        </HudRow>
        {showAway && (
          <HudRow label="Away">
            <FormationDropdown
              team="away"
              current={away}
              customs={listCustomFormations()}
              onChange={(codeOrId) => handleApplyFormation(codeOrId, 'away')}
              onSaveCustom={(name) => {
                saveCustomFormation({
                  name,
                  slots: awaySpots.map((s) => ({ role: s.role, x: s.x, z: s.z })),
                });
              }}
            />
          </HudRow>
        )}
      </HudShell>}
      {/* Drag-to-pick — after a drag commits to a different zone, the
          coach explicitly confirms (or changes) the new slot. */}
      {slotPicker && (
        <PositionSlotPicker
          currentSlot={slotPicker.currentSlot}
          suggested={slotPicker.suggested}
          onPick={(slot) => {
            const [side, idxStr] = slotPicker.key.split('-');
            const idx = Number(idxStr);
            if (side === 'home') {
              setHomeSpots((prev) =>
                prev.map((s, i) => (i === idx ? { ...s, role: slot } : s)),
              );
            } else if (side === 'away') {
              setAwaySpots((prev) =>
                prev.map((s, i) => (i === idx ? { ...s, role: slot } : s)),
              );
            }
            setSlotPicker(null);
          }}
          onClose={() => setSlotPicker(null)}
        />
      )}
      <PatternDrawer
        open={patternsOpen}
        onClose={() => setPatternsOpen(false)}
        onSelectPattern={handleSelectPattern}
      />
      {/* Phase 4 §3 — formation library drawer. Hidden until the
          coach taps the Formations chip in the Design row. */}
      <FormationLibraryDrawer
        open={formationsOpen}
        onClose={() => setFormationsOpen(false)}
        onApply={handleApplyFormation}
        currentTeam="home"
      />
      <PlayerBriefsDrawer
        open={briefsOpen}
        onClose={() => setBriefsOpen(false)}
        briefs={playerBriefs}
        spots={homeSpots}
        side="home"
        layoutStale={playerBriefsStale}
        onRegenerate={handleRegenerateBriefs}
        onEdit={handleEditBrief}
        busy={briefsBusy}
        history={playerBriefHistory}
        onRestore={handleRestoreBrief}
        roster={roster}
        onRosterEdit={(key, next) => {
          setRoster((prev) => {
            const noName = next.name === undefined || next.name === '';
            const noNumber = next.number === undefined;
            if (noName && noNumber) {
              const { [key]: _drop, ...rest } = prev;
              return rest;
            }
            return { ...prev, [key]: next };
          });
        }}
        onImportRoster={() => {
          const players = loadLocalPlayers();
          if (players.length === 0) {
            window.alert(
              'No players found in the team roster. Add some on the Players page first.',
            );
            return;
          }
          setRoster((prev) => {
            const next = { ...prev };
            for (let i = 0; i < homeSpots.length; i++) {
              const player = players[i];
              if (!player) break;
              const entry = next[`home-${i}`] ?? {};
              next[`home-${i}`] = {
                ...entry,
                name: player.name,
              };
            }
            return next;
          });
        }}
        rosterHref={
          persistKey?.startsWith('tactics-3d-')
            ? `/tactics/${persistKey.slice('tactics-3d-'.length)}/briefs`
            : undefined
        }
      />
      {/* Phase 4 §6 — team tactics panel. We seed the bundle lazily
          on first open so empty layouts don't carry a default
          5-slot bundle until the coach actually engages with it. */}
      {tacticsPanelOpen && teamTactics && (
        <TeamTacticsPanel
          bundle={teamTactics}
          team={tacticsPanelOpen}
          onChange={(next) => {
            teamShapeChangedRef.current = true;
            setTeamTactics(next);
          }}
          onClose={() => setTacticsPanelOpen(false)}
        />
      )}
      {/* Phase 4 §6.5 — set-piece drawer. `initialType` comes from the
          `?type=` deep-link param so /setpieces "+ Create" lands on the
          right tab. */}
      <SetPieceDrawer
        open={setPiecesOpen}
        onClose={() => setSetPiecesOpen(false)}
        onApply={handleApplySetPiece}
        initialType={initialSetPieceType}
      />
      {/* Phase 4 §5 — role picker. Mounts when a coach clicks a token
          in role mode. */}
      {roleTarget && (
        <RoleInstructionsPicker
          tokenKey={roleTarget.key}
          slot={roleTarget.slot}
          current={playerRoles[roleTarget.key]}
          onChange={(next) =>
            setPlayerRoles((prev) => ({ ...prev, [roleTarget.key]: next }))
          }
          onClose={() => setRoleTarget(null)}
        />
      )}
    </div>
  );
}
